// WhatsApp Business API Service
// Supports: Twilio and Interakt providers

const WHATSAPP_PROVIDER = process.env.REACT_APP_WHATSAPP_PROVIDER || 'interakt';

class WhatsAppService {
  constructor() {
    this.provider = WHATSAPP_PROVIDER;
    this.apiKey = process.env.REACT_APP_WHATSAPP_API_KEY;
    this.phoneNumber = process.env.REACT_APP_WHATSAPP_PHONE_NUMBER;

    // Twilio credentials
    this.twilioAccountSid = process.env.REACT_APP_TWILIO_ACCOUNT_SID;
    this.twilioAuthToken = process.env.REACT_APP_TWILIO_AUTH_TOKEN;
    this.twilioWhatsAppNumber = process.env.REACT_APP_TWILIO_WHATSAPP_NUMBER;

    console.log(`📱 WhatsApp Service initialized with provider: ${this.provider}`);
  }

  /**
   * Send order confirmation message
   */
  async sendOrderConfirmation(orderData) {
    const { customer, orderNumber, items, total, paymentMethod } = orderData;

    const message = this._formatOrderConfirmation(orderNumber, items, total, paymentMethod);

    return await this.sendMessage(customer.phone, message, 'order_confirmation');
  }

  /**
   * Send order status update
   */
  async sendOrderStatusUpdate(orderData, newStatus) {
    const { customer, orderNumber, customOrderId } = orderData;
    const orderId = customOrderId || orderNumber;

    const statusMessages = {
      'confirmed': `✅ *Order Accepted!*\n\nYour order #${orderId} has been confirmed by the restaurant.\n\n🍳 We're preparing your delicious meal!\n\nEstimated time: 30-40 minutes`,
      'in_progress': `🍳 *Cooking in Progress*\n\nYour order #${orderId} is being prepared with love!\n\n👨‍🍳 Our chef is working on your meal.\n\nWe'll notify you when it's ready.`,
      'prepared': `✅ *Order Ready!*\n\nGreat news! Your order #${orderId} is ready!\n\n📦 ${orderData.orderType === 'delivery' ? 'Will be delivered soon' : 'Ready for pickup'}\n\n${orderData.orderType === 'pickup' ? '📍 Pickup Address:\n' + (orderData.outletAddress || 'Restaurant location') : ''}`,
      'out_for_delivery': `🚴 *Out for Delivery*\n\nYour order #${orderId} is on the way!\n\n🛵 Delivery partner is heading to your location.\n\n📍 Track your order: ${this._generateTrackingLink(orderId)}`,
      'delivered': `🎉 *Order Delivered!*\n\nYour order #${orderId} has been delivered!\n\n😋 Enjoy your meal!\n\n⭐ Rate your experience: ${this._generateReviewLink(orderId)}`,
      'cancelled': `❌ *Order Cancelled*\n\nYour order #${orderId} has been cancelled.\n\n${orderData.cancellationReason || 'If you didn\'t request this, please contact us.'}\n\n📞 Call us: +917873611363`
    };

    const message = statusMessages[newStatus] || `Order #${orderId} status updated to: ${newStatus}`;

    return await this.sendMessage(customer.phone, message, 'status_update');
  }

  /**
   * Send delivery person assigned notification
   */
  async sendDeliveryAssigned(orderData, deliveryBoyData) {
    const { customer, orderNumber } = orderData;
    const { name, phone } = deliveryBoyData;

    const message = `🚴 ${name} is delivering your order #${orderNumber}!\n\n` +
      `Contact: ${phone}\n` +
      `Track live location in the app.`;

    return await this.sendMessage(customer.phone, message, 'delivery_assigned');
  }

  /**
   * Send ETA update
   */
  async sendETAUpdate(orderData, etaMinutes) {
    const { customer, orderNumber } = orderData;

    const message = `⏰ Your order #${orderNumber} will arrive in approximately ${etaMinutes} minutes!`;

    return await this.sendMessage(customer.phone, message, 'eta_update');
  }

  /**
   * Send message to customer
   * @param {string} phoneNumber - Customer phone (e.g., +917873611363)
   * @param {string} message - Message text
   * @param {string} messageType - Type for tracking
   */
  async sendMessage(phoneNumber, message, messageType = 'general') {
    try {
      // Format phone number (ensure +91 prefix for India)
      const formattedPhone = this._formatPhoneNumber(phoneNumber);

      console.log(`📤 Sending WhatsApp to ${formattedPhone} (${messageType})`);

      if (this.provider === 'twilio') {
        return await this._sendViaTwilio(formattedPhone, message);
      } else if (this.provider === 'interakt') {
        return await this._sendViaInterakt(formattedPhone, message);
      } else {
        console.warn('⚠️ WhatsApp provider not configured, falling back to browser WhatsApp');
        return this._sendViaBrowser(formattedPhone, message);
      }
    } catch (error) {
      console.error('❌ WhatsApp send error:', error);
      return {
        success: false,
        error: error.message,
        fallback: 'sms'
      };
    }
  }

  /**
   * Send via Twilio WhatsApp API
   */
  async _sendViaTwilio(phoneNumber, message) {
    if (!this.twilioAccountSid || !this.twilioAuthToken) {
      throw new Error('Twilio credentials not configured');
    }

    const url = `https://api.twilio.com/2010-04-01/Accounts/${this.twilioAccountSid}/Messages.json`;

    const credentials = btoa(`${this.twilioAccountSid}:${this.twilioAuthToken}`);

    const formData = new URLSearchParams();
    formData.append('From', this.twilioWhatsAppNumber); // e.g., whatsapp:+14155238886
    formData.append('To', `whatsapp:${phoneNumber}`);
    formData.append('Body', message);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: formData
    });

    const data = await response.json();

    if (response.ok) {
      console.log('✅ WhatsApp sent via Twilio:', data.sid);
      return {
        success: true,
        provider: 'twilio',
        messageId: data.sid,
        status: data.status
      };
    } else {
      throw new Error(data.message || 'Twilio API error');
    }
  }

  /**
   * Send via Interakt WhatsApp API
   */
  async _sendViaInterakt(phoneNumber, message) {
    if (!this.apiKey) {
      throw new Error('Interakt API key not configured');
    }

    const url = 'https://api.interakt.ai/v1/public/message/';

    const payload = {
      countryCode: '+91',
      phoneNumber: phoneNumber.replace('+91', ''),
      type: 'Template',
      template: {
        name: 'order_update', // You need to create this template in Interakt
        languageCode: 'en',
        bodyValues: [message]
      }
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${this.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (response.ok) {
      console.log('✅ WhatsApp sent via Interakt:', data.result.messageId);
      return {
        success: true,
        provider: 'interakt',
        messageId: data.result.messageId
      };
    } else {
      throw new Error(data.message || 'Interakt API error');
    }
  }

  /**
   * Fallback: Open WhatsApp Web (browser redirect)
   */
  _sendViaBrowser(phoneNumber, message) {
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${encodedMessage}`;

    console.log('📱 Opening WhatsApp in browser:', whatsappUrl);

    // Don't auto-open in production, just return URL
    return {
      success: true,
      provider: 'browser',
      url: whatsappUrl,
      action: 'manual' // Requires user to click
    };
  }

  /**
   * Format order confirmation message
   */
  _formatOrderConfirmation(orderNumber, items, total, paymentMethod) {
    let message = `✅ *Order Confirmed!*\n\n`;
    message += `Order Number: *#${orderNumber}*\n\n`;
    message += `*Items:*\n`;

    items.forEach(item => {
      message += `• ${item.name} x ${item.quantity} - ₹${item.price * item.quantity}\n`;
    });

    message += `\n*Total: ₹${total}*\n`;
    message += `Payment: ${paymentMethod === 'online' ? '✅ Paid Online' : '💵 Cash on Delivery'}\n\n`;
    message += `We'll notify you when your order is ready! 🍽️`;

    return message;
  }

  /**
   * Format phone number to E.164 format
   */
  _formatPhoneNumber(phone) {
    // Remove all non-digits
    let cleaned = phone.replace(/\D/g, '');

    // If starts with 91, ensure + prefix
    if (cleaned.startsWith('91')) {
      return '+' + cleaned;
    }

    // If 10 digits, add +91 prefix
    if (cleaned.length === 10) {
      return '+91' + cleaned;
    }

    // Otherwise return as-is with + prefix
    return '+' + cleaned;
  }

  /**
   * Send order bill via WhatsApp
   */
  async sendOrderBill(orderData) {
    const { customer, customOrderId, orderNumber } = orderData;
    const orderId = customOrderId || orderNumber;

    const message = this._formatOrderBill(orderData);

    return await this.sendMessage(customer.phone, message, 'order_bill');
  }

  /**
   * Send UPI payment QR code for COD orders
   */
  async sendPaymentQRCode(orderData) {
    const { customer, customOrderId, orderNumber, total } = orderData;
    const orderId = customOrderId || orderNumber;

    const upiId = process.env.REACT_APP_UPI_ID || 'odishazaika@upi';
    const merchantName = process.env.REACT_APP_MERCHANT_NAME || 'Odisha Zaika';

    const message = this._formatPaymentQRMessage(orderId, total, upiId, merchantName);

    return await this.sendMessage(customer.phone, message, 'payment_qr');
  }

  /**
   * Format complete order bill
   */
  _formatOrderBill(orderData) {
    const {
      customOrderId,
      orderNumber,
      items,
      subtotal,
      tax,
      taxRate,
      deliveryFee,
      total,
      paymentMethod,
      orderType,
      customerName,
      deliveryAddress,
      outletName,
      outletAddress,
      createdAt
    } = orderData;

    const orderId = customOrderId || orderNumber;
    const orderDate = new Date(createdAt).toLocaleString('en-IN');

    let bill = `🧾 *ORDER BILL*\n`;
    bill += `━━━━━━━━━━━━━━━━━━━━\n\n`;
    bill += `📦 Order ID: *#${orderId}*\n`;
    bill += `📅 Date: ${orderDate}\n`;
    bill += `👤 Customer: ${customerName}\n`;
    bill += `🏪 Outlet: ${outletName}\n\n`;

    bill += `📍 *${orderType === 'delivery' ? 'Delivery Address' : 'Pickup Location'}:*\n`;
    bill += `${orderType === 'delivery' ? deliveryAddress : outletAddress}\n\n`;

    bill += `━━━━━━━━━━━━━━━━━━━━\n`;
    bill += `🍽️ *ITEMS ORDERED*\n`;
    bill += `━━━━━━━━━━━━━━━━━━━━\n\n`;

    items.forEach((item, index) => {
      const vegIcon = item.isVeg ? '🟢' : '🔴';
      bill += `${index + 1}. ${vegIcon} ${item.name}\n`;
      bill += `   ${item.quantity} × ₹${item.price} = ₹${item.quantity * item.price}\n\n`;
    });

    bill += `━━━━━━━━━━━━━━━━━━━━\n`;
    bill += `💰 *PAYMENT DETAILS*\n`;
    bill += `━━━━━━━━━━━━━━━━━━━━\n\n`;
    bill += `Subtotal:          ₹${subtotal}\n`;
    bill += `Tax (${(taxRate * 100).toFixed(0)}%):           ₹${tax.toFixed(2)}\n`;
    if (orderType === 'delivery') {
      bill += `Delivery Fee:      ${deliveryFee > 0 ? '₹' + deliveryFee : 'FREE'}\n`;
    }
    bill += `━━━━━━━━━━━━━━━━━━━━\n`;
    bill += `*TOTAL:            ₹${total}*\n`;
    bill += `━━━━━━━━━━━━━━━━━━━━\n\n`;

    bill += `💳 Payment Method: *${paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment'}*\n\n`;

    if (paymentMethod === 'cod') {
      bill += `💡 *Tip:* You can pay via UPI at delivery!\n`;
      bill += `We'll share our UPI QR code.\n\n`;
    }

    bill += `━━━━━━━━━━━━━━━━━━━━\n`;
    bill += `🙏 Thank you for ordering!\n`;
    bill += `📞 Support: +917873611363\n`;
    bill += `🌐 www.odishazaika.com`;

    return bill;
  }

  /**
   * Format payment QR code message
   */
  _formatPaymentQRMessage(orderId, amount, upiId, merchantName) {
    // Generate UPI deep link
    const upiLink = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(`Order ${orderId}`)}`;

    let message = `💳 *PAY VIA UPI*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
    message += `Order ID: *#${orderId}*\n`;
    message += `Amount: *₹${amount}*\n\n`;
    message += `🔗 Pay using this UPI link:\n`;
    message += `${upiLink}\n\n`;
    message += `*OR*\n\n`;
    message += `📱 Scan QR code from our delivery partner\n\n`;
    message += `💰 UPI ID: \`${upiId}\`\n`;
    message += `📝 Add note: Order ${orderId}\n\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `✅ Accepted: GPay, PhonePe, Paytm, BHIM\n\n`;
    message += `🙏 Thank you!`;

    return message;
  }

  /**
   * Generate tracking link for order
   */
  _generateTrackingLink(orderId) {
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    return `${baseUrl}/track-order?orderId=${orderId}`;
  }

  /**
   * Generate review link for completed order
   */
  _generateReviewLink(orderId) {
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    return `${baseUrl}/review?orderId=${orderId}`;
  }

  /**
   * Check if WhatsApp is configured
   */
  isConfigured() {
    if (this.provider === 'twilio') {
      return !!(this.twilioAccountSid && this.twilioAuthToken && this.twilioWhatsAppNumber);
    } else if (this.provider === 'interakt') {
      return !!(this.apiKey && this.phoneNumber);
    }
    return false;
  }
}

// Singleton instance
const whatsappService = new WhatsAppService();

export default whatsappService;
